import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, ArrowLeft } from 'lucide-react';
import { useHasPermission } from '@/client/hooks/useHasPermission';

const LoadingFallback = () => (
  <div className="flex min-h-[60vh] items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900" />
  </div>
);

const AccessRestricted: React.FC = () => {
  const navigate = useNavigate();
  return (
    <div className="flex min-h-[70vh] w-full items-center justify-center px-6 py-16">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100">
          <Lock className="h-8 w-8 text-gray-400" />
        </div>
        <h1 className="mb-2 text-2xl font-bold text-gray-900">You don’t have access to this section</h1>
        <p className="mb-8 text-sm text-gray-600">
          This area is restricted. If you believe you should have access, ask an administrator to grant you the
          required permission.
        </p>
        <button
          onClick={() => navigate('/admin')}
          className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-800"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to dashboard
        </button>
      </div>
    </div>
  );
};

/** Gates a route/subtree behind a permission code. Superadmins always pass. */
const RequirePermission: React.FC<{ code: string; children: React.ReactNode }> = ({ code, children }) => {
  const { hasPermission, isSuperAdmin, isLoading } = useHasPermission(code);

  if (isLoading) return <LoadingFallback />;
  if (!isSuperAdmin && !hasPermission) return <AccessRestricted />;

  return <>{children}</>;
};

export default RequirePermission;
